import React, { useState } from 'react';
import { 
  ClipboardDocumentListIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserIcon,
  EyeIcon,
  WrenchScrewdriverIcon,
  CogIcon,
  ShieldExclamationIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { useWorksheets } from '../../hooks/useWorksheets';
import { useCustomers } from '../../hooks/useCustomers';
import { useLanguage } from '../../contexts/LanguageContext';
import { Worksheet, CreateWorksheetData } from '../../types/worksheet';
import LoadingSpinner from '../common/LoadingSpinner';
import { Tooltip } from '../common/Tooltip';
import WorksheetForm from './WorksheetForm';
import WorksheetView from './WorksheetView';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import toast from 'react-hot-toast';
import Pagination from '../common/Pagination';

interface WorksheetListProps {
  triggerNewForm?: boolean;
  onFormOpened?: () => void;
  onDeleteClick?: (worksheet: Worksheet) => void;
  searchTerm?: string;
}

const WorksheetList: React.FC<WorksheetListProps> = ({
  triggerNewForm = false,
  onFormOpened,
  onDeleteClick,
  searchTerm = ''
}) => {
  const { worksheets: hookWorksheets, loading, deleteWorksheet, addWorksheet, updateWorksheet } = useWorksheets();
  const worksheets = hookWorksheets; // Mindig a hook adatait használjuk
  const { customers } = useCustomers();
  const { t } = useLanguage();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedWorksheet, setSelectedWorksheet] = useState<Worksheet | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Helper function to safely format dates
  const formatDate = (date: any) => {
    if (!date) return '';
    
    try {
      // Handle Firestore Timestamp objects
      if (date && typeof date.toDate === 'function') {
        return format(date.toDate(), 'yyyy. MM. dd.', { locale: hu });
      }
      
      // Handle Date objects
      if (date instanceof Date && !isNaN(date.getTime())) {
        return format(date, 'yyyy. MM. dd.', { locale: hu });
      }
      
      // Handle date strings
      if (typeof date === 'string') {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
          return format(parsedDate, 'yyyy. MM. dd.', { locale: hu });
        }
      }
      
      return '';
    } catch (error) {
      console.error('Date formatting error:', error, date);
      return '';
    }
  };

  // Helper function to safely format dates for desktop view
  const formatDateShort = (date: any) => {
    if (!date) return '';
    
    try {
      // Handle Firestore Timestamp objects
      if (date && typeof date.toDate === 'function') {
        return format(date.toDate(), 'MM/dd', { locale: hu });
      }
      
      // Handle Date objects
      if (date instanceof Date && !isNaN(date.getTime())) {
        return format(date, 'MM/dd', { locale: hu });
      }
      
      // Handle date strings
      if (typeof date === 'string') {
        const parsedDate = new Date(date);
        if (!isNaN(parsedDate.getTime())) {
          return format(parsedDate, 'MM/dd', { locale: hu });
        }
      }
      
      return '';
    } catch (error) {
      console.error('Date formatting error:', error, date);
      return '';
    }
  };

  // Handle triggerNewForm prop
  React.useEffect(() => {
    if (triggerNewForm) {
      setSelectedWorksheet(null);
      setIsFormOpen(true);
      onFormOpened?.();
    }
  }, [triggerNewForm, onFormOpened]);

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return 'Ismeretlen ügyfél';
    
    return customer.type === 'organization' 
      ? customer.companyName 
      : `${customer.firstName} ${customer.lastName}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
      case 'in_progress': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700';
      case 'completed': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      case 'cancelled': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      default: return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return <ClipboardDocumentListIcon className="h-4 w-4" />;
      case 'in_progress': return <ClockIcon className="h-4 w-4" />;
      case 'completed': return <CheckCircleIcon className="h-4 w-4" />;
      case 'cancelled': return <XCircleIcon className="h-4 w-4" />;
      default: return <ClipboardDocumentListIcon className="h-4 w-4" />;
    }
  };

  const getWorkTypeIcon = (type: string) => {
    switch (type) {
      case 'installation': return <WrenchScrewdriverIcon className="h-4 w-4" />;
      case 'maintenance': return <CogIcon className="h-4 w-4" />;
      case 'repair': return <WrenchScrewdriverIcon className="h-4 w-4" />;
      case 'inspection': return <MagnifyingGlassIcon className="h-4 w-4" />;
      case 'other': return <ClipboardDocumentListIcon className="h-4 w-4" />;
      default: return <ClipboardDocumentListIcon className="h-4 w-4" />;
    }
  };

  const getWorkTypeLabel = (type: string) => {
    return t(`worksheets.workTypes.${type}`);
  };

  const getWorkTypeColor = (type: string) => {
    switch (type) {
      case 'installation': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700';
      case 'maintenance': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      case 'repair': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      case 'inspection': return 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-700';
      case 'other': return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
      default: return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      case 'medium': return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700';
      case 'low': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      default: return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
    }
  };

  const getStatusColorHex = (status: string) => {
    switch (status) {
      case 'draft': return '#6b7280';
      case 'in_progress': return '#2563eb';
      case 'completed': return '#059669';
      case 'cancelled': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getPriorityColorHex = (priority: string) => {
    switch (priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#d97706';
      case 'low': return '#059669';
      default: return '#6b7280';
    }
  };

  const getWorkTypeColorHex = (type: string) => {
    switch (type) {
      case 'installation': return '#2563eb';
      case 'maintenance': return '#059669';
      case 'repair': return '#dc2626';
      case 'inspection': return '#7c3aed';
      case 'other': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const filteredWorksheets = worksheets.filter(worksheet => {
    const statusMatch = filterStatus === 'all' || worksheet.status === filterStatus;
    const typeMatch = filterType === 'all' || worksheet.workType === filterType;
    const priorityMatch = filterPriority === 'all' || worksheet.priority === filterPriority;
    
    // Search functionality
    let searchMatch = true;
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      const customerName = getCustomerName(worksheet.customerId).toLowerCase();
      const workTypeLabel = (getWorkTypeLabel(worksheet.workType) || '').toLowerCase();
      
      searchMatch = 
        worksheet.title.toLowerCase().includes(query) ||
        worksheet.worksheetNumber.toLowerCase().includes(query) ||
        customerName.includes(query) ||
        workTypeLabel.includes(query) ||
        (worksheet.description?.toLowerCase().includes(query) || false) ||
        (worksheet.location?.toLowerCase().includes(query) || false);
    }
    
    return statusMatch && typeMatch && priorityMatch && searchMatch;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredWorksheets.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentWorksheets = filteredWorksheets.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleEdit = (worksheet: Worksheet) => {
    setSelectedWorksheet(worksheet);
    setIsFormOpen(true);
  };

  const handleView = (worksheet: Worksheet) => {
    setSelectedWorksheet(worksheet);
    setIsViewOpen(true);
  };

  const handleDelete = async (worksheet: Worksheet) => {
    try {
      await deleteWorksheet(worksheet.id);
      toast.success(t('worksheets.messages.deleteSuccess'));
    } catch (error) {
      toast.error(t('worksheets.messages.deleteError'));
    }
  };

  const handleFormSubmit = async (data: CreateWorksheetData) => {
    try {
      if (selectedWorksheet) {
        await updateWorksheet(selectedWorksheet.id, data);
        toast.success(t('worksheets.messages.updateSuccess'));
      } else {
        await addWorksheet(data);
        toast.success(t('worksheets.messages.createSuccess'));
      }
      setIsFormOpen(false);
      setSelectedWorksheet(null);
    } catch (error) {
      toast.error(
        selectedWorksheet 
          ? t('worksheets.messages.updateError')
          : t('worksheets.messages.createError')
      );
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setSelectedWorksheet(null);
  };

  const handleStatusChange = async (worksheet: Worksheet, newStatus: string) => {
    try {
      await updateWorksheet(worksheet.id, {
        ...worksheet,
        status: newStatus as any
      });
      toast.success(t('worksheets.messages.updateSuccess'));
    } catch (error) {
      console.error('Error updating worksheet status:', error);
      toast.error(t('worksheets.messages.updateError'));
    }
  };

  const handlePriorityChange = async (worksheet: Worksheet, newPriority: string) => {
    try {
      await updateWorksheet(worksheet.id, {
        ...worksheet,
        priority: newPriority as any
      });
      toast.success(t('worksheets.messages.updateSuccess'));
    } catch (error) {
      console.error('Error updating worksheet priority:', error);
      toast.error(t('worksheets.messages.updateError'));
    }
  };

  const handleWorkTypeChange = async (worksheet: Worksheet, newWorkType: string) => {
    try {
      await updateWorksheet(worksheet.id, {
        ...worksheet,
        workType: newWorkType as any
      });
      toast.success(t('worksheets.messages.updateSuccess'));
    } catch (error) {
      console.error('Error updating worksheet work type:', error);
      toast.error(t('worksheets.messages.updateError'));
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Status filters */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('worksheets.fields.status')}</h3>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border ${
                  filterStatus === 'all'
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {t('worksheets.filters.all')}
              </button>
              <button
                onClick={() => setFilterStatus('draft')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterStatus === 'draft'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterStatus === 'draft' ? getStatusColorHex('draft') : getStatusColorHex('draft') + '30',
                  color: filterStatus === 'draft' ? '#ffffff' : getStatusColorHex('draft')
                }}
              >
                {t('worksheets.status.draft')}
              </button>
              <button
                onClick={() => setFilterStatus('in_progress')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterStatus === 'in_progress'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterStatus === 'in_progress' ? getStatusColorHex('in_progress') : getStatusColorHex('in_progress') + '30',
                  color: filterStatus === 'in_progress' ? '#ffffff' : getStatusColorHex('in_progress')
                }}
              >
                {t('worksheets.status.in_progress')}
              </button>
              <button
                onClick={() => setFilterStatus('completed')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterStatus === 'completed'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterStatus === 'completed' ? getStatusColorHex('completed') : getStatusColorHex('completed') + '30',
                  color: filterStatus === 'completed' ? '#ffffff' : getStatusColorHex('completed')
                }}
              >
                {t('worksheets.status.completed')}
              </button>
              <button
                onClick={() => setFilterStatus('cancelled')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterStatus === 'cancelled'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterStatus === 'cancelled' ? getStatusColorHex('cancelled') : getStatusColorHex('cancelled') + '30',
                  color: filterStatus === 'cancelled' ? '#ffffff' : getStatusColorHex('cancelled')
                }}
              >
                {t('worksheets.status.cancelled')}
              </button>
            </div>
          </div>

          {/* Type filters */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('worksheets.fields.workType')}</h3>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setFilterType('all')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border ${
                  filterType === 'all'
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {t('worksheets.filters.allTypes')}
              </button>
              <button
                onClick={() => setFilterType('installation')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterType === 'installation'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterType === 'installation' ? getWorkTypeColorHex('installation') : getWorkTypeColorHex('installation') + '30',
                  color: filterType === 'installation' ? '#ffffff' : getWorkTypeColorHex('installation')
                }}
              >
                {t('worksheets.workTypes.installation')}
              </button>
              <button
                onClick={() => setFilterType('maintenance')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterType === 'maintenance'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterType === 'maintenance' ? getWorkTypeColorHex('maintenance') : getWorkTypeColorHex('maintenance') + '30',
                  color: filterType === 'maintenance' ? '#ffffff' : getWorkTypeColorHex('maintenance')
                }}
              >
                {t('worksheets.workTypes.maintenance')}
              </button>
              <button
                onClick={() => setFilterType('repair')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterType === 'repair'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterType === 'repair' ? getWorkTypeColorHex('repair') : getWorkTypeColorHex('repair') + '30',
                  color: filterType === 'repair' ? '#ffffff' : getWorkTypeColorHex('repair')
                }}
              >
                {t('worksheets.workTypes.repair')}
              </button>
              <button
                onClick={() => setFilterType('inspection')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterType === 'inspection'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterType === 'inspection' ? getWorkTypeColorHex('inspection') : getWorkTypeColorHex('inspection') + '30',
                  color: filterType === 'inspection' ? '#ffffff' : getWorkTypeColorHex('inspection')
                }}
              >
                {t('worksheets.workTypes.inspection')}
              </button>
            </div>
          </div>

          {/* Priority filters */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('worksheets.fields.priority')}</h3>
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setFilterPriority('all')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border ${
                  filterPriority === 'all'
                    ? 'bg-slate-600 text-white border-slate-600'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {t('worksheets.filters.all')}
              </button>
              <button
                onClick={() => setFilterPriority('high')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterPriority === 'high'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterPriority === 'high' ? getPriorityColorHex('high') : getPriorityColorHex('high') + '30',
                  color: filterPriority === 'high' ? '#ffffff' : getPriorityColorHex('high')
                }}
              >
                {t('worksheets.priority.high')}
              </button>
              <button
                onClick={() => setFilterPriority('medium')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterPriority === 'medium'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterPriority === 'medium' ? getPriorityColorHex('medium') : getPriorityColorHex('medium') + '30',
                  color: filterPriority === 'medium' ? '#ffffff' : getPriorityColorHex('medium')
                }}
              >
                {t('worksheets.priority.medium')}
              </button>
              <button
                onClick={() => setFilterPriority('low')}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-colors border-0 ${
                  filterPriority === 'low'
                    ? 'text-white'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: filterPriority === 'low' ? getPriorityColorHex('low') : getPriorityColorHex('low') + '30',
                  color: filterPriority === 'low' ? '#ffffff' : getPriorityColorHex('low')
                }}
              >
                {t('worksheets.priority.low')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop table view */}
      <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('worksheets.fields.title')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('worksheets.fields.customer')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('worksheets.fields.workType')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('worksheets.fields.status')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('worksheets.fields.priority')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t('worksheets.fields.dueDate')}
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">
                  Részletek
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300 w-20">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {currentWorksheets.map(worksheet => (
                <tr key={worksheet.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => handleView(worksheet)}>
                  <td className="px-3 py-2">
                    <div className="flex items-center">
                      <div className="p-1.5 rounded bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-700 border mr-2">
                        <ClipboardDocumentListIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          {worksheet.title}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          {worksheet.worksheetNumber}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col">
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        {getCustomerName(worksheet.customerId)}
                      </div>
                      {worksheet.location && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {worksheet.location}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      <select
                        value={worksheet.workType}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleWorkTypeChange(worksheet, e.target.value)}
                        className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                        style={{
                          backgroundColor: getWorkTypeColorHex(worksheet.workType) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                          color: getWorkTypeColorHex(worksheet.workType)
                        }}
                      >
                        <option value="installation">{t('worksheets.workTypes.installation')}</option>
                        <option value="maintenance">{t('worksheets.workTypes.maintenance')}</option>
                        <option value="repair">{t('worksheets.workTypes.repair')}</option>
                        <option value="inspection">{t('worksheets.workTypes.inspection')}</option>
                        <option value="other">{t('worksheets.workTypes.other')}</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      <select
                        value={worksheet.status}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleStatusChange(worksheet, e.target.value)}
                        className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                        style={{
                          backgroundColor: getStatusColorHex(worksheet.status) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                          color: getStatusColorHex(worksheet.status)
                        }}
                      >
                        <option value="draft">{t('worksheets.status.draft')}</option>
                        <option value="in_progress">{t('worksheets.status.in_progress')}</option>
                        <option value="completed">{t('worksheets.status.completed')}</option>
                        <option value="cancelled">{t('worksheets.status.cancelled')}</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      <select
                        value={worksheet.priority}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handlePriorityChange(worksheet, e.target.value)}
                        className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                        style={{
                          backgroundColor: getPriorityColorHex(worksheet.priority) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                          color: getPriorityColorHex(worksheet.priority)
                        }}
                      >
                        <option value="low">{t('worksheets.priority.low')}</option>
                        <option value="medium">{t('worksheets.priority.medium')}</option>
                        <option value="high">{t('worksheets.priority.high')}</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      {worksheet.dueDate && (
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          {formatDateShort(worksheet.dueDate)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-center">
                      <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
                        {worksheet.deploymentOccurred && (
                          <div className="flex items-center">
                            <TruckIcon className="h-3 w-3 text-green-600 dark:text-green-400 mr-1" />
                            <span>Kiszállás</span>
                          </div>
                        )}
                        {worksheet.travelKmTotal && (
                          <div className="flex items-center">
                            <span className="text-blue-600 dark:text-blue-400 font-medium">{worksheet.travelKmTotal} km</span>
                          </div>
                        )}
                        
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end space-x-2">
                      <Tooltip content={t('worksheets.actions.view')}>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleView(worksheet); }}
                          className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content={t('worksheets.actions.edit')}>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(worksheet); }}
                          className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content={t('worksheets.actions.delete')}>
                        <button
                          onClick={(e) => { e.stopPropagation(); onDeleteClick?.(worksheet); }}
                          className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile list view */}
      <div className="lg:hidden space-y-2 mb-2 w-full overflow-hidden">
        {currentWorksheets.map(worksheet => (
          <div key={worksheet.id} className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-3 shadow-md border border-gray-200 dark:border-gray-700 w-full overflow-hidden cursor-pointer" onClick={() => handleView(worksheet)}>
            <div className="flex justify-between w-full">
              <div className="flex items-center mr-2 mb-1.5 sm:mb-2 max-w-[60%]">
                <div className="p-1 sm:p-1.5 rounded bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-700 border mr-1 sm:mr-2 flex-shrink-0">
                  <ClipboardDocumentListIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="min-w-0 w-full overflow-hidden">
                  <div className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {worksheet.title}
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 truncate">
                    {worksheet.worksheetNumber}
                  </div>
                </div>
              </div>

              <div className="flex items-center flex-shrink-0 ml-auto space-x-0 sm:space-x-1">
                <select
                  value={worksheet.priority}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => handlePriorityChange(worksheet, e.target.value)}
                  className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                  style={{
                    backgroundColor: getPriorityColorHex(worksheet.priority) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                    color: getPriorityColorHex(worksheet.priority)
                  }}
                >
                  <option value="low">{t('worksheets.priority.low')}</option>
                  <option value="medium">{t('worksheets.priority.medium')}</option>
                  <option value="high">{t('worksheets.priority.high')}</option>
                </select>
                <button
                  onClick={(e) => { e.stopPropagation(); handleView(worksheet); }}
                  className="p-1 rounded-full text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                >
                  <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleEdit(worksheet); }}
                  className="p-1 rounded-full text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                >
                  <PencilSquareIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDeleteClick?.(worksheet); }}
                  className="p-1 rounded-full text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
            </div>

            <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] sm:text-xs overflow-hidden">
              <div className="flex items-center text-gray-600 dark:text-gray-300">
                <UserIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 flex-shrink-0" />
                <span className="truncate">{getCustomerName(worksheet.customerId)}</span>
              </div>
              <div className="flex items-center">
                <select
                  value={worksheet.workType}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => handleWorkTypeChange(worksheet, e.target.value)}
                  className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                  style={{
                    backgroundColor: getWorkTypeColorHex(worksheet.workType) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                    color: getWorkTypeColorHex(worksheet.workType)
                  }}
                >
                  <option value="installation">{t('worksheets.workTypes.installation')}</option>
                  <option value="maintenance">{t('worksheets.workTypes.maintenance')}</option>
                  <option value="repair">{t('worksheets.workTypes.repair')}</option>
                  <option value="inspection">{t('worksheets.workTypes.inspection')}</option>
                  <option value="other">{t('worksheets.workTypes.other')}</option>
                </select>
              </div>
                             {worksheet.worksheetDate && (
                 <div className="flex items-center text-gray-600 dark:text-gray-300">
                   <CalendarIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 flex-shrink-0" />
                   <span className="truncate">{formatDate(worksheet.worksheetDate)}</span>
                 </div>
               )}
            </div>

            {/* New fields row for mobile */}
            <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] sm:text-xs overflow-hidden">
              {worksheet.deploymentOccurred && (
                <div className="flex items-center text-green-600 dark:text-green-400">
                  <TruckIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 flex-shrink-0" />
                  <span className="truncate">Kiszállás</span>
                </div>
              )}
              {worksheet.travelKmTotal && (
                <div className="flex items-center text-blue-600 dark:text-blue-400">
                  <span className="font-medium">{worksheet.travelKmTotal} km</span>
                </div>
              )}
              
              {worksheet.startTime && worksheet.endTime && (
                <div className="flex items-center text-gray-600 dark:text-gray-300">
                  <ClockIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 flex-shrink-0" />
                  <span className="truncate">{worksheet.startTime} - {worksheet.endTime}</span>
                </div>
              )}
            </div>

            <div className="mt-1.5 sm:mt-2 overflow-hidden">
              <select
                value={worksheet.status}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleStatusChange(worksheet, e.target.value)}
                className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                style={{
                  backgroundColor: getStatusColorHex(worksheet.status) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                  color: getStatusColorHex(worksheet.status)
                }}
              >
                <option value="draft">{t('worksheets.status.draft')}</option>
                <option value="in_progress">{t('worksheets.status.in_progress')}</option>
                <option value="completed">{t('worksheets.status.completed')}</option>
                <option value="cancelled">{t('worksheets.status.cancelled')}</option>
              </select>
            </div>

            {worksheet.description && (
              <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 p-1.5 sm:p-2 rounded overflow-hidden">
                <div className="line-clamp-2">{worksheet.description}</div>
              </div>
            )}

            <div className="mt-2 sm:mt-3 flex justify-end space-x-1 sm:space-x-2">
              <button
                onClick={(e) => { e.stopPropagation(); handleView(worksheet); }}
                className="text-xs sm:text-sm px-2 py-1 rounded-md bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/30"
              >
                {t('worksheets.actions.view')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination controls */}
      <div className="relative z-10">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={filteredWorksheets.length}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
        />
      </div>

      {/* Empty state */}
      {filteredWorksheets.length === 0 && (
        <div className="text-center py-12">
          <ClipboardDocumentListIcon className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('worksheets.messages.noWorksheets')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('worksheets.messages.noWorksheetsDescription')}
          </p>
        </div>
      )}

      {/* Worksheet Form */}
      <WorksheetForm
        isOpen={isFormOpen}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
        initialData={selectedWorksheet}
      />

      {/* Worksheet View */}
      {isViewOpen && selectedWorksheet && (
        <WorksheetView
          worksheet={selectedWorksheet}
          isOpen={isViewOpen}
          onClose={() => {
            setIsViewOpen(false);
            setSelectedWorksheet(null);
          }}
          onEdit={handleEdit}
        />
      )}
    </div>
  );
};

export default WorksheetList; 